// Shared in-progress indicator. Lifted out of WorkspacePage so the session rail can show the
// same thing — a job that is 40% done should look 40% done wherever it appears, and a second
// implementation is how the two drift into disagreeing about the same job.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { playCompletionChimeOnce } from "../utils/sfx";
import { COUNTDOWN_TOTAL_VIDEO, COUNTDOWN_TOTAL_OTHER } from "./GenerationQueue";
import { autoplayVideo } from "../utils/autoplayVideo";

/* The in-progress indicator: a black tile with a slow sheen passing over it and a Magi-blue
   line filling across its bottom edge. No text.
 *
   THERE IS NO CLOCK IN HERE ANY MORE, and that is worth stating because it removes the last
   per-second render in the grid. The line's position IS the remaining time, drawn continuously
   instead of announced once a second, so nothing in this component needs to re-render after it
   mounts — the whole indicator is two declarative CSS animations.

   The line is the video idiom — a bar at the foot of a thumbnail means progress — and blue is
   what makes that placement work. In white it disappeared: the card's boundary with a white
   page, its outline and its shadow all live within a few pixels of that line, and one more
   white thing among them read as part of the frame.

   WHAT IS NOT HERE, AND WHY. A dial went through conic pie, thin ring and filled wedge at
   sizes from 26 to 132px, and every version drew the complaint "jagged" or "laggy". The cause
   was never the drawing, it was the speed: an edge sweeping a 47px circle over a 60s estimate
   advances about 2.5px per second — well under a pixel per frame — so what reaches the eye is
   an edge snapping to the next pixel a couple of times a second. The same progress across a
   ~390px card moves about 6.5px per second, comfortably above the threshold where slow motion
   reads as motion.

   BOTH ANIMATIONS ARE `transform` ONLY, so the compositor owns them and a busy main thread
   cannot stutter either. The line is one animation for the whole generation, seeked with a
   negative delay to where the job already is, so a card mounted halfway through picks up in
   the right place.

   REQUIRES A POSITIONED PARENT: everything here is absolute against the tile, and all four
   callers wrap it in something with `relative`. */
/* THE LOADING LOOP, IN FOUR COLOURS, ONE PER JOB IN ROTATION.
 *
 * A generating tile plays a short muted clip of a morphing form behind the progress line — the
 * Clash-of-Clans idea, where the wait is a thing to look at rather than a void. The form is a
 * spherical-harmonic blob, Y3^0 handed back and forth with Y3^2 over one exact period so it
 * loops with no cut and no ping-pong, rendered once per colour on the near-black plate by
 * `tools/showcase/dropzone_harmonics_lab.py` (variant "b").
 *
 * The pole term is ODD in cos(theta), which is the whole reason this one was picked over the
 * original Y4^0 pairing: an even term makes both ends of the form do the same thing at once,
 * so it pulses symmetrically, where an odd one makes it LEAN — one end swelling as the other
 * narrows. Several higher-order pairs were tried first and all read as lumpy organic matter;
 * every step up in order adds lobes and concavity, and what makes this family work is staying
 * convex and few-lobed.
 *
 * ROTATING THE COLOUR IS WHAT MAKES A QUEUE READABLE. Four jobs in flight all playing the same
 * clip look like one thing rendered four times — the eye cannot tell which card is which, and
 * the cards are not otherwise distinguishable until their thumbnails land. A different colour
 * per job gives each one an identity for the minute it exists.
 *
 * The rotation is per-user and persists: the assignments live in localStorage keyed by each
 * job's STABLE id, so a job keeps its colour when its card re-renders, when it appears on two
 * surfaces at once, when its temp id is swapped for the real one a second in, and across a
 * reload. See the note on loopColorFor — both of those were bugs before they were features.
 *
 * Each clip is ~75KB. Browsers share the download but not the decode, so the rotation also
 * means four jobs decode four different files — still trivial at this size, but the reason not
 * to grow the set much further.
 */
const LOOP_COLORS = ["blue", "red", "white", "green"];
const LOOP_ASSIGN_KEY = "magi_loading_colors";
const LOOP_ASSIGN_MAX = 40;   // a generous ceiling on jobs anyone has in flight at once

/* THE ASSIGNMENT IS PERSISTED, NOT JUST MEMOISED, and it is keyed on the job's STABLE id.
 * Two bugs lived here, both showing up as "the colour keeps changing":
 *
 *   1. It was keyed on `job.id`, which CHANGES. A job is created under a temp id and the real
 *      one is swapped in about a second later, so every job was looked up twice under two
 *      different keys — and the second lookup, finding nothing pinned, took the next colour in
 *      the cycle. Every card changed colour a second after it appeared, and burned two slots of
 *      the rotation doing it. `uid` is the id a job is given once and keeps (see
 *      useGenerationQueue.addJob), which is exactly what this needs.
 *
 *   2. The pin lived in a module Map, so a reload lost every assignment while the CYCLE
 *      COUNTER survived in localStorage — in-flight jobs came back in different colours, and
 *      each one advanced the counter again on the way.
 *
 * Storing the map itself fixes both: a job's colour is decided once, survives a reload, and the
 * counter only moves for jobs that have never been seen. Trimmed to the most recent entries so
 * this cannot grow without bound.
 */
const readAssignments = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(LOOP_ASSIGN_KEY) || "{}");
    return raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
  } catch {
    return {};   // private mode, or someone else's value under the key
  }
};

/* THE COUNTER IS STORED, NOT DERIVED FROM THE MAP'S SIZE, and that is the whole fix for
 * "why are they all the same colour".
 *
 * The rotation used to be `LOOP_COLORS[Object.keys(assigned).length % 4]`. That is a correct
 * cycle only while the map is still growing. It is trimmed to LOOP_ASSIGN_MAX, so once a
 * person has generated that many videos the length is PINNED at 40 for ever — and 40 % 4 is
 * 0, so every job from the 42nd onwards came out blue. Four cards generating side by side
 * were four identical blue blobs, which is exactly the thing the rotation exists to prevent.
 *
 * Simulated over 46 assignments: jobs 1-41 rotate correctly, 42 onwards are blue, blue, blue.
 *
 * `__n` rather than a second localStorage key so one read and one write still cover both, and
 * a legacy map (40 entries, no counter) migrates for free: it reads as 0 and starts cycling
 * from blue again. The key cannot collide with a job id, which is a uuid, a `p<N>` or a
 * `temp-...`. It is excluded from the trim so it is never aged out of its own map. */
const LOOP_COUNTER_KEY = "__n";

const loopColorFor = (jobKey) => {
  if (!jobKey) return LOOP_COLORS[0];
  const assigned = readAssignments();
  if (assigned[jobKey]) return assigned[jobKey];

  const n = Number(assigned[LOOP_COUNTER_KEY]) || 0;
  const color = LOOP_COLORS[n % LOOP_COLORS.length];
  assigned[jobKey] = color;

  // Insertion order is preserved for string keys, so the oldest are at the front.
  const keys = Object.keys(assigned).filter((k) => k !== LOOP_COUNTER_KEY);
  const kept = keys.length > LOOP_ASSIGN_MAX
    ? keys.slice(keys.length - LOOP_ASSIGN_MAX)
    : keys;
  const trimmed = Object.fromEntries(kept.map((k) => [k, assigned[k]]));
  trimmed[LOOP_COUNTER_KEY] = n + 1;
  try { localStorage.setItem(LOOP_ASSIGN_KEY, JSON.stringify(trimmed)); } catch { /* ignore */ }
  return color;
};

const ProgressDial = ({ job }) => {
  const done = job.state === "completed";
  const queued = job.state === "queued";
  const startRef = useRef(
    job.processingStartedAt ? new Date(job.processingStartedAt).getTime() : Date.now(),
  );
  useEffect(() => {
    if (job.processingStartedAt) startRef.current = new Date(job.processingStartedAt).getTime();
  }, [job.processingStartedAt]);

  // Same estimate the queue panel uses, including its per-failed-scene penalty.
  const failed = (job.scenes || []).filter((sc) => sc.status === "failed").length;
  const kind = job.metadata?.projectType || job.type;
  const base = job.estimatedSeconds
    || (kind === "video" ? COUNTDOWN_TOTAL_VIDEO : COUNTDOWN_TOTAL_OTHER);
  const total = base + failed * 60;

  /* THE SOUND FIRES FROM HERE TOO, not only from the queue panel. The panel is the thing most
     people keep minimised or never open — and it only knows about jobs in ITS list, so a
     generation you came back to on a reload landed in silence while its tile sat in plain
     sight. Deduped by job id, so when both surfaces see the same job land it plays once. The
     id is whatever this caller's shape carries: the Workspace grid passes `id`, the session
     rail passes an entry with `job_id`. */
  const jobId = job.id || job.jobId || job.job_id || job.key;
  useEffect(() => {
    if (done) playCompletionChimeOnce(jobId);
  }, [done, jobId]);

  /* Declared ONCE per mount, from the job's own start time. Every earlier version of this
     indicator recomputed its animation from a ticking value, which restarted the animation on
     every tick — that was the jaggedness, not the shape. */
  const fill = useMemo(() => ({
    "--bar-dur": `${total}s`,
    // Negative delay = seek. A job 30s into a 60s estimate starts its line half way across.
    "--bar-delay": `-${Math.max(0, (Date.now() - startRef.current) / 1000)}s`,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [total, startRef.current]);

  // A clip that 404s or cannot be decoded takes itself off the tile rather than leaving a
  // broken element over the card.
  const [loopFailed, setLoopFailed] = useState(false);
  /* `uid` FIRST — the id that never changes. `jobId` above is deliberately the current id,
     because the chime dedupes across surfaces that only know the real one; the colour needs
     the opposite property. A session entry has no uid and falls back to its job_id, which is
     stable for its whole life anyway. */
  const loopKey = job.uid || job.id || job.jobId || job.job_id || job.key;
  const loopSrc = `/loading/loop-${loopColorFor(loopKey)}.mp4`;
  /* THE LOOP RUNS FOR AS LONG AS THE TILE EXISTS, including after the job is done.
   *
   * It used to stop on `done`, on the reasoning stated below for the sheen: the tile is about
   * to be replaced by the real thumbnail, and a surface still moving under a finished job
   * contradicts it. That is true only if "about to" means the next frame. It does not — the
   * tile is deliberately HELD until its video shows up in the grid (see `held` in
   * WorkspacePage), and on a fresh page load the grid starts EMPTY, so every recently
   * finished job sat there as a featureless black rectangle with a full progress bar until
   * the first library fetch came back. Reported as "what are these two blank thumbnails".
   *
   * A tile that has gone blank says less than one that is still playing: the loop plus a
   * filled bar reads as "done, just landing", which is exactly what it is. The sheen stays
   * off on `done` — that one is layered under the clip and has nothing to add. */
  const showLoop = !loopFailed;

  return (
    <>
      <div
        className={`ws-gen${done ? " ws-gen-done" : ""}${showLoop ? " ws-gen-lit" : ""}`}
        aria-hidden="true"
      >
        {showLoop && (
          <video
            ref={autoplayVideo}
            className="ws-gen-loop"
            src={loopSrc}
            muted
            loop
            autoPlay
            playsInline
            /* `metadata`, not `auto`: several of these can be on screen at once and a card that
               is about to be replaced by a real thumbnail should not be pulling the whole file
               ahead of the videos the user actually asked for. */
            preload="metadata"
            onError={() => setLoopFailed(true)}
          />
        )}
      </div>
      {/* Queued has nothing to report yet: a track under a job that has not started would be a
          progress bar claiming 0%, which is a different statement from "not begun". */}
      {!queued && (
        <div className="ws-bar" aria-hidden="true">
          <span
            className={done ? "ws-bar-fill ws-bar-done" : "ws-bar-fill ws-bar-run"}
            style={done ? undefined : fill}
          />
        </div>
      )}
    </>
  );
};

export default ProgressDial;
